import { Fragment, useCallback, useRef, useState } from "react";
import { useAppSelector } from '../../redux/hooks';
import { Container, Dropdown, Nav, Navbar, Offcanvas, Stack } from "react-bootstrap";
import { useNavigate } from "react-router";
import { Paths } from "../routes/Routes";
import { useAppDispatch } from "../../redux/hooks";
import { clearToken, getAppId } from "../../redux/tokenSlice";
import { clearAppState } from "../../redux/appStateSlice";
import SignOutModal from "./SignOutModal";
import { ApolloConsumer, useLazyQuery } from '@apollo/client';
import { Logout } from '../../graphQL/api';
import { replace } from 'lodash';
import { clearBackNavigation, clearSelectedGroup } from "../../redux/navigationSlice";
import { persistor } from "../../redux/store";
import { GET_MISSED_ACKNOWLEDGMENTS_CSV_REPORT } from "../../graphQL/queries";
import { buildMissedAcknowledgmentsCsv, IMissingAcknowledgmentsReportItem } from "../../helpers/managerHelper";

const NavigationBar = ({title, isHome}) => {
	const [showSideMenu, setShowSideMenu] = useState(false);
	const [showSignOutModal, setShowSignOutModal] = useState(false)
	const currentUser = useAppSelector(state => state.appState.currentUser);
	const currentSelectedGroup = useAppSelector(state => state.nav.currentSelectedGroup)
	const navigate = useNavigate()
	const dispatch = useAppDispatch();
	const bondedPairToken = useAppSelector(state => state.token.bondedPairToken)
	const backNavigation = useAppSelector(state => state.nav.backNavigation)

	const apolloClient = useRef<any>()
	const currentOrganization = useAppSelector(state => state.appState.currentOrganization);

	const [getMissedAcknowledgmentsCsvReport, getMissedAcknowledgmentsCsvReportResponses] = useLazyQuery(GET_MISSED_ACKNOWLEDGMENTS_CSV_REPORT);

	const handleDownloadMissedAcknowledgments = useCallback(async (e) => {
		e.preventDefault();
		setShowSideMenu(false);
		const result = await getMissedAcknowledgmentsCsvReport({
			variables: { groupID: null }
		});
		const report = (result.data?.missingAcknowledgmentsReport ?? []) as IMissingAcknowledgmentsReportItem[];
		const { csv, filename } = buildMissedAcknowledgmentsCsv(
			report,
			currentOrganization?.startViewableWindow,
			currentOrganization?.endViewableWindow,
			currentOrganization?.name
		);
		const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = filename;
		a.click();
		URL.revokeObjectURL(url);
	}, [currentOrganization, getMissedAcknowledgmentsCsvReport]);

	function toggleSidMenu() {
		setShowSideMenu(!showSideMenu)
	}

	function navigateToHome(e) {
		e.preventDefault();
		navigate(Paths.HOME)
		dispatch(clearBackNavigation())
	}

	function navigateBack(e) {
		e.preventDefault();
		navigate(backNavigation)
		dispatch(clearBackNavigation())
	}

	function navigateToMissedAcknowledgments(e) {
		e.preventDefault();

		const path = replace(Paths.MANAGER_MISSED_ACKNOWLEDGMENTS, ':groupid', currentSelectedGroup?.groupID);
		navigate(path);
	}

	function navigateToAbout(e) {
		e.preventDefault();
		navigate(Paths.ABOUT);
		dispatch(clearBackNavigation());
	}

	function promptForSignOut(e, client) {
		e.preventDefault();

		apolloClient.current = client

		setShowSideMenu(false)
		setShowSignOutModal(true)
	}

	async function signOutClicked() {
		setShowSignOutModal(false)
		setShowSideMenu(false);

		try {
			// 1. Clear Apollo cache first to prevent queries from using stale data
			if (apolloClient.current) {
				await apolloClient.current.resetStore();
			}

			// 2. Clear Redux state
			dispatch(clearToken());
			dispatch(clearAppState());
			dispatch(clearBackNavigation());
			dispatch(clearSelectedGroup());

			// 3. Purge persisted storage
			await persistor.purge();

			// 4. Call logout API (fire and forget)
			if (bondedPairToken) {
				Logout(bondedPairToken, getAppId(), response => {});
			}

			// 5. Navigate after all cleanup is complete
			navigate(Paths.LOGIN);
		} catch (error) {
			console.error('Error during sign out:', error);
			// Still navigate even if cleanup fails to prevent user from being stuck
			navigate(Paths.LOGIN);
		}
	}

	function renderDivider() {
		return <Dropdown.Divider style={{opacity:0.6, marginLeft:-13, marginRight:-13}}/>
	}

	return (
		<Navbar collapseOnSelect key={'asdas'} className="navbar-toggler" expand={false} sticky="top" style={{backgroundColor:'#212121', marginLeft: -10, height:50, position:'fixed', top:0, width:'100%', zIndex:100}} variant="dark">
			<Container fluid>
				<Stack direction="horizontal" gap={2} style={{alignItems: 'center'}}>
					{ isHome ?
						<button className="navbar-toggler" type="button" onClick={ e => { e.preventDefault(); toggleSidMenu(); } } 
							aria-label="Toggle navigation" style={{border: "none !important", padding: "0.25rem 0.75rem"}}> <i className="fas fa-bars"></i>
						</button>
						:
						<button className="navbar-toggler" type="button" onClick={ e => navigateBack(e) }
							aria-label="back navigation" style={{border: "none !important", padding: "0.25rem 0.75rem"}}> <i className="fas fa-chevron-left"></i>
						</button>
					}
					
					<span style={{color:'white'}}>{title ? title : ""}</span> 
				</Stack>

				<Navbar.Offcanvas
					id={`offcanvasNavbar-expand-123aas`}
					aria-labelledby={`offcanvasNavbarLabel-expand-25233`}
					placement="top"
					style={{color:'white'}}
					show={showSideMenu}
					onHide={() => setShowSideMenu(false)}
				>
					<Offcanvas.Header >
						<Offcanvas.Title id={`offcanvasNavbarLabel-expand-65623432`}>
							Menu
						</Offcanvas.Title>
						<i onClick={ e => setShowSideMenu(false)} className="fa-solid fa-xmark"/>
					</Offcanvas.Header>
					<Offcanvas.Body>
						{ renderDivider() }
						<Nav className="justify-content-end flex-grow-1 pe-3">
							<Nav.Link style={{color:'white'}} onClick={ e => { setShowSideMenu(false); navigateToHome(e)} }>Home</Nav.Link>
						</Nav>
						{ renderDivider() }
						{ currentUser && !currentUser.isInDriverRole &&
							<Fragment>
								<Nav className="justify-content-end flex-grow-1 pe-3">
									<Nav.Link style={{color:'white'}} onClick={ e => { setShowSideMenu(false); navigateToMissedAcknowledgments(e)} }>Missed Acknowledgments</Nav.Link>
								</Nav>
								{ renderDivider() }
								<Nav className="justify-content-end flex-grow-1 pe-3">
									<Nav.Link
										style={{color:'white'}}
										onClick={handleDownloadMissedAcknowledgments}
										disabled={getMissedAcknowledgmentsCsvReportResponses.loading}
									>
										<i className="fa fa-download me-2" aria-hidden="true" />
										{getMissedAcknowledgmentsCsvReportResponses.loading ? 'Downloading...' : 'All Missed Acknowledgments'}
									</Nav.Link>
								</Nav>
								{ renderDivider() }
							</Fragment>
						}
						<Nav className="justify-content-end flex-grow-1 pe-3">
							<Nav.Link style={{color:'white'}} onClick={ e => { setShowSideMenu(false); navigateToAbout(e)} }>About</Nav.Link>
						</Nav>
						{ renderDivider() }
						<Nav className="justify-content-end flex-grow-1 pe-3">
							<ApolloConsumer>
								{ client =>
									<Nav.Link style={{color:'white'}} onClick={ e => promptForSignOut(e, client) }>Sign Out</Nav.Link>
								}
							</ApolloConsumer>
							
						</Nav>
					</Offcanvas.Body>
				</Navbar.Offcanvas>
				{ showSignOutModal && <SignOutModal shouldShow={showSignOutModal} onSignOut={()=>signOutClicked()} onClose={()=>setShowSignOutModal(false) } />}
			</Container>
		</Navbar>
	)
}

export default NavigationBar;